import React, { useState, useEffect } from 'react';
import { 
  DollarSign, 
  Shield,
  Building,
  Users,
  AlertTriangle,
  AlertCircle,
  RefreshCw,
  CheckCircle,
  Clock,
  Activity,
  Plus,
  Download,
  Eye,
  Filter,
  Calendar,
  TrendingUp,
  TrendingDown,
  Loader,
  X,
  Trash2,
  Save
} from 'lucide-react';
import { 
  collection, 
  getDocs, 
  addDoc, 
  deleteDoc, 
  query, 
  where,
  onSnapshot,
  doc,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase';

// Import Firebase Invoice Service
import {
  getInvoices,
  subscribeToInvoices,
  setCompanyContext
} from '../services/firebaseInvoiceService';

const FinanceManager = ({ 
  currentUser, 
  currentCompany, 
  getCompanyContext, 
  hasPermission 
}) => {
  // Core state
  const [bikes, setBikes] = useState([]);
  const [invoiceData, setInvoiceData] = useState([]);
  const [expenseData, setExpenseData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [expenseCategories, setExpenseCategories] = useState([
    'Maintenance',
    'Fuel',
    'Insurance',
    'Registration',
    'Repairs',
    'Cleaning',
    'Accessories',
    'Parts',
    'Labor',
    'Storage',
    'Other'
  ]);
  
  // UI state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [lastSyncTime, setLastSyncTime] = useState(new Date());
  const [showFilters, setShowFilters] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  
  // Expense form state
  const [expenseForm, setExpenseForm] = useState({
    bikeId: '',
    category: 'Maintenance',
    amount: '',
    date: new Date().toISOString().split('T')[0],
    description: '',
    notes: ''
  });
  
  // Filter state
  const [selectedBike, setSelectedBike] = useState('all');
  const [selectedMonth, setSelectedMonth] = useState('all');
  const [selectedYear, setSelectedYear] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIncomeType, setSelectedIncomeType] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [viewMode, setViewMode] = useState('all');

  // Get current company context
  const companyId = getCompanyContext();

  // Firestore collections
  const managedBikesCollection = collection(db, 'managedBikes');
  const expensesCollection = collection(db, 'bikeExpenses');

  // ============================================================================
  // HELPER FUNCTIONS
  // ============================================================================

  // Helper function to convert Firebase Timestamp to ISO string or Date
  const convertFirebaseDate = (dateValue) => {
    if (!dateValue) {
      return new Date().toISOString();
    }
    
    // Check if it's a Firebase Timestamp (has toDate method or seconds property)
    if (dateValue && typeof dateValue === 'object') {
      // Firebase Timestamp with toDate method
      if (typeof dateValue.toDate === 'function') {
        return dateValue.toDate().toISOString();
      }
      // Firebase Timestamp with seconds property
      if (dateValue.seconds) {
        return new Date(dateValue.seconds * 1000).toISOString();
      }
      // Already a Date object
      if (dateValue instanceof Date) {
        return dateValue.toISOString();
      }
    }
    
    // It's probably already a string
    if (typeof dateValue === 'string') {
      return dateValue;
    }
    
    // Fallback to current date
    console.warn('Could not convert date value:', dateValue);
    return new Date().toISOString();
  };

  // Helper function to safely get bike reference from invoice
  const getBikeReference = (invoice) => {
    return invoice.motorbikeNumber || 
           invoice.bikeNumber || 
           invoice.bikeRegNumber || 
           invoice.bikeModel ||
           'N/A';
  };

  // Get bike model from invoice - FIXED TO USE THE BIKES ARRAY FOR LOOKUP
  const getBikeModel = (invoice) => {
    const motorbikeRef = getBikeReference(invoice);
    
    // If we have bikes loaded, try to find the model
    if (motorbikeRef && bikes && bikes.length > 0) {
      const bike = bikes.find(b => 
        b.regNumber === motorbikeRef || 
        b.id === motorbikeRef ||
        b.regNumber?.toLowerCase() === motorbikeRef?.toLowerCase()
      );
      
      if (bike && bike.model) {
        return bike.model;
      }
    }

    return 'N/A';
  };

  // Get bike registration number
  const getBikeRegNumber = (invoice) => {
    return invoice.motorbikeNumber || invoice.bikeRegNumber || 'N/A';
  };

  // Get invoice amount - Try multiple fields
  const getInvoiceAmount = (invoice) => {
    const amount = parseFloat(
      invoice.totalAmount || 
      invoice.rentalAmount || 
      invoice.extendedAmount || 
      invoice.baseCost || 
      invoice.totalCost || 
      invoice.amount ||
      0
    );

    return amount;
  };

  // Format date for display
  const formatDate = (dateString) => {
    try {
      if (!dateString) {
        return 'N/A';
      }

      const convertedDate = convertFirebaseDate(dateString);
      const date = new Date(convertedDate);
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        console.warn('Invalid date after conversion:', dateString, convertedDate);
        return 'N/A';
      }

      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', error, dateString);
      return 'N/A';
    }
  };

  // ============================================================================
  // DATA LOADING
  // ============================================================================

  // Permission check and initialization
  useEffect(() => {
    if (!hasPermission('financeManager')) {
      setError('You do not have permission to access Finance Manager');
      setLoading(false);
      return;
    }

    if (!companyId) {
      setError('No company context available');
      setLoading(false);
      return;
    }

    const initializeData = async () => {
      try {
        console.log(`FinanceManager: Initializing data for company ${companyId}`);
        
        if (currentCompany?.name) {
          setCompanyContext(companyId, currentCompany.name);
        }
        
        await loadInitialData();
        
      } catch (error) {
        console.error('FinanceManager: Error initializing data:', error);
        setError('Failed to initialize finance manager: ' + error.message);
        setLoading(false);
      }
    };

    initializeData();
  }, [companyId, hasPermission, currentCompany]);

  // Load initial data
  const loadInitialData = async () => {
    try {
      setLoading(true);
      console.log('🔄 Loading initial data...');

      // Load bikes FIRST (needed for bike model lookup)
      await loadCompanyBikes();
      
      // Then load invoices
      await loadInvoiceData();
      
      // Load expenses
      await loadExpenseData();

      setLastSyncTime(new Date());
      setLoading(false);
      
      console.log('✅ Initial data loaded successfully');
      
    } catch (error) {
      console.error('❌ Error loading initial data:', error);
      setError('Failed to load data: ' + error.message);
      setLoading(false);
    }
  };

  // Load company bikes
  const loadCompanyBikes = async () => {
    if (!companyId) throw new Error('Company ID is required');

    try {
      console.log(`🏍️ Loading bikes for company ${companyId}`);
      const q = query(managedBikesCollection, where('companyId', '==', companyId));
      const querySnapshot = await getDocs(q);
      const loadedBikes = [];
      
      querySnapshot.forEach((doc) => {
        const bikeData = { id: doc.id, ...doc.data() };
        loadedBikes.push(bikeData);
        console.log('  - Loaded bike:', bikeData.regNumber, bikeData.model);
      });
      
      console.log(`✅ Loaded ${loadedBikes.length} bikes`);
      setBikes(loadedBikes);
      
    } catch (error) {
      console.error('❌ Error loading bikes:', error);
      setBikes([]);
      throw error;
    }
  };

  // Load invoice data
  const loadInvoiceData = async () => {
    if (!companyId) throw new Error('Company ID is required');

    try {
      console.log(`🧾 Loading invoices for company: ${companyId}`);
      const invoices = await getInvoices(companyId);
      
      console.log(`✅ Loaded ${invoices.length} invoices`);
      
      setInvoiceData(invoices);
    } catch (error) {
      console.error('❌ Error loading invoices:', error);
      setInvoiceData([]);
      throw error;
    }
  };

  // Load expense data
  const loadExpenseData = async () => {
    if (!companyId) throw new Error('Company ID is required');

    try {
      console.log(`💸 Loading expenses for company: ${companyId}`);
      const q = query(expensesCollection, where('companyId', '==', companyId));
      const querySnapshot = await getDocs(q);
      const expenses = [];
      
      querySnapshot.forEach((doc) => {
        expenses.push({ id: doc.id, ...doc.data() });
      });
      
      console.log(`✅ Loaded ${expenses.length} expenses`);
      setExpenseData(expenses);
      
    } catch (error) {
      console.error('❌ Error loading expenses:', error);
      setExpenseData([]);
      throw error;
    }
  };

  // Force refresh all data
  const forceRefresh = async () => {
    setLoading(true);
    try {
      await loadInitialData();
      alert('Data refreshed successfully!');
    } catch (error) {
      alert('Failed to refresh data: ' + error.message);
    }
  };

  // ============================================================================
  // EXPENSE MANAGEMENT
  // ============================================================================

  // Reset expense form
  const resetExpenseForm = () => {
    setExpenseForm({
      bikeId: '',
      category: 'Maintenance',
      amount: '',
      date: new Date().toISOString().split('T')[0],
      description: '',
      notes: ''
    });
  };

  // Handle expense form change
  const handleExpenseFormChange = (field, value) => {
    setExpenseForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Add new expense
  const handleAddExpense = async () => {
    try {
      // Validation
      if (!expenseForm.bikeId) {
        alert('Please select a bike');
        return;
      }
      if (!expenseForm.amount || parseFloat(expenseForm.amount) <= 0) {
        alert('Please enter a valid amount');
        return;
      }
      if (!expenseForm.description) {
        alert('Please enter a description');
        return;
      }

      setSaving(true);

      // Find the selected bike
      const selectedBikeData = bikes.find(b => b.id === expenseForm.bikeId);
      
      if (!selectedBikeData) {
        throw new Error('Selected bike not found');
      }

      // Create expense object
      const expenseData = {
        companyId: companyId,
        bikeId: expenseForm.bikeId,
        bikeRegNumber: selectedBikeData.regNumber,
        bikeModel: selectedBikeData.model,
        category: expenseForm.category,
        amount: parseFloat(expenseForm.amount),
        date: expenseForm.date,
        description: expenseForm.description,
        notes: expenseForm.notes || '',
        createdAt: serverTimestamp(),
        createdBy: currentUser?.username || 'Unknown',
        createdById: currentUser?.id || 'unknown'
      };

      // Save to Firestore
      await addDoc(expensesCollection, expenseData);

      console.log('✅ Expense added successfully');
      
      // Reload expenses
      await loadExpenseData();
      
      // Close modal and reset form
      setShowExpenseModal(false);
      resetExpenseForm();
      
      alert('Expense added successfully!');
      
    } catch (error) {
      console.error('❌ Error adding expense:', error);
      alert('Failed to add expense: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  // Delete expense
  const handleDeleteExpense = async (expenseId) => {
    if (!window.confirm('Are you sure you want to delete this expense?')) {
      return;
    }

    try {
      setSaving(true);
      await deleteDoc(doc(db, 'bikeExpenses', expenseId));
      console.log('✅ Expense deleted successfully');
      
      // Reload expenses
      await loadExpenseData();
      
      alert('Expense deleted successfully!');
    } catch (error) {
      console.error('❌ Error deleting expense:', error);
      alert('Failed to delete expense: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  // ============================================================================
  // DATA PROCESSING & FILTERING
  // ============================================================================

  // Calculate summary statistics
  const calculateSummary = () => {
    const incomeRecords = filteredData.filter(r => r.type === 'income');
    const expenseRecords = filteredData.filter(r => r.type === 'expense');
    
    const totalIncome = incomeRecords.reduce((sum, r) => sum + r.amount, 0);
    const totalExpenses = expenseRecords.reduce((sum, r) => sum + Math.abs(r.amount), 0);
    const netProfit = totalIncome - totalExpenses;
    
    return {
      totalIncome,
      totalExpenses,
      netProfit,
      incomeCount: incomeRecords.length,
      expenseCount: expenseRecords.length,
      recordCount: filteredData.length
    };
  };

  // Get unique bikes for filter dropdown
  const getUniqueBikes = () => {
    const bikeSet = new Set();
    
    // From invoices
    invoiceData.forEach(invoice => {
      const bikeRef = getBikeReference(invoice);
      if (bikeRef && bikeRef !== 'N/A') {
        bikeSet.add(bikeRef);
      }
    });
    
    // From expenses
    expenseData.forEach(expense => {
      if (expense.bikeRegNumber && expense.bikeRegNumber !== 'N/A') {
        bikeSet.add(expense.bikeRegNumber);
      }
    });
    
    return Array.from(bikeSet).sort();
  };

  // Apply filters and sorting
  useEffect(() => {
    try {
      console.log('🔍 Applying filters...');
      
      // Combine invoices and expenses into unified records
      const incomeRecords = invoiceData.map(invoice => ({
        id: invoice.id,
        type: 'income',
        date: invoice.createdAt || invoice.pickupDateTime || new Date().toISOString(),
        description: invoice.invoiceNumber || 'Invoice',
        amount: getInvoiceAmount(invoice),
        bikeModel: getBikeModel(invoice),
        motorbikeNumber: getBikeReference(invoice),
        customerName: invoice.customerName,
        invoice: invoice,
        category: invoice.isExtendedRental ? 'Extended Rental' : 'Regular Rental'
      }));

      const expenseRecords = expenseData.map(expense => ({
        id: expense.id,
        type: 'expense',
        date: expense.date || expense.createdAt || new Date().toISOString(),
        description: expense.description,
        amount: expense.amount,
        bikeModel: expense.bikeModel || 'N/A',
        motorbikeNumber: expense.bikeRegNumber || 'N/A',
        category: expense.category,
        notes: expense.notes,
        expense: expense
      }));

      let combined = [...incomeRecords, ...expenseRecords];

      // Apply view mode filter
      if (viewMode === 'income') {
        combined = combined.filter(r => r.type === 'income');
      } else if (viewMode === 'expenses') {
        combined = combined.filter(r => r.type === 'expense');
      }

      // Apply bike filter
      if (selectedBike !== 'all') {
        combined = combined.filter(r => r.motorbikeNumber === selectedBike);
      }

      // Apply search filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        combined = combined.filter(r => 
          r.description?.toLowerCase().includes(searchLower) ||
          r.bikeModel?.toLowerCase().includes(searchLower) ||
          r.motorbikeNumber?.toLowerCase().includes(searchLower) ||
          r.customerName?.toLowerCase().includes(searchLower) ||
          r.category?.toLowerCase().includes(searchLower)
        );
      }

      // Apply month filter
      if (selectedMonth !== 'all') {
        combined = combined.filter(r => {
          const date = new Date(convertFirebaseDate(r.date));
          return date.getMonth() === parseInt(selectedMonth);
        });
      }

      // Apply year filter
      if (selectedYear !== 'all') {
        combined = combined.filter(r => {
          const date = new Date(convertFirebaseDate(r.date));
          return date.getFullYear() === parseInt(selectedYear);
        });
      }

      // Apply income type filter
      if (selectedIncomeType !== 'all') {
        combined = combined.filter(r => 
          r.type === 'income' && r.category === selectedIncomeType
        );
      }

      // Sort records
      combined.sort((a, b) => {
        const dateA = new Date(convertFirebaseDate(a.date));
        const dateB = new Date(convertFirebaseDate(b.date));
        
        if (sortBy === 'date') {
          return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
        } else if (sortBy === 'amount') {
          return sortOrder === 'desc' ? b.amount - a.amount : a.amount - b.amount;
        }
        
        return 0;
      });

      console.log(`✅ Filtered ${combined.length} records`);
      setFilteredData(combined);
      
    } catch (error) {
      console.error('❌ Error filtering data:', error);
      setFilteredData([]);
    }
  }, [invoiceData, expenseData, bikes, viewMode, searchTerm, selectedBike, selectedMonth, selectedYear, selectedIncomeType, sortBy, sortOrder]);

  // ============================================================================
  // EXPORT FUNCTIONALITY
  // ============================================================================

  const exportToCSV = () => {
    try {
      const headers = ['Date', 'Type', 'Description', 'Bike Model', 'Bike Number', 'Category', 'Amount'];
      const rows = filteredData.map(record => [
        formatDate(record.date),
        record.type,
        record.description,
        record.bikeModel,
        record.motorbikeNumber,
        record.category || '',
        (record.type === 'income' ? '+' : '-') + record.amount.toFixed(2)
      ]);

      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `finance-report-${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      
      console.log('✅ CSV exported successfully');
    } catch (error) {
      console.error('❌ Error exporting CSV:', error);
      alert('Failed to export CSV: ' + error.message);
    }
  };

  // ============================================================================
  // INVOICE VIEWING
  // ============================================================================

  const handleViewInvoice = (record) => {
    setSelectedInvoice(record.invoice);
    setShowInvoiceModal(true);
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  // Permission check
  if (error && error.includes('permission')) {
    return (
      <div className="max-w-7xl mx-auto p-6 bg-gradient-to-br from-gray-900 to-blue-900 text-white min-h-screen">
        <div className="text-center py-12">
          <Shield className="mx-auto h-20 w-20 text-red-400 mb-6" />
          <h2 className="text-2xl font-bold text-white mb-4">Access Denied</h2>
          <p className="text-gray-300">{error}</p>
        </div>
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className="max-w-7xl mx-auto p-6 bg-gradient-to-br from-gray-900 to-blue-900 text-white min-h-screen overflow-y-auto">
        <div className="flex items-center justify-center min-h-64">
          <div className="text-center">
            <Loader className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto mb-4" />
            <p className="text-gray-400">Loading financial data from Firebase...</p>
            {currentCompany && (
              <p className="text-gray-500 text-sm mt-2">Company: {currentCompany.name}</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  const summary = calculateSummary();

  return (
    <div className="max-w-7xl mx-auto p-6 bg-gradient-to-br from-gray-900 to-blue-900 text-white min-h-screen">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className="bg-green-600 p-3 rounded-lg">
              <DollarSign size={32} className="text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white">Finance Manager</h1>
              <p className="text-gray-300">Track income and expenses with real-time Firebase integration</p>
              <div className="flex items-center gap-4 mt-1 flex-wrap">
                <p className="text-gray-400 text-sm">
                  ✅ {bikes.length} bikes • {invoiceData.length} invoices • {expenseData.length} expenses
                </p>
                {currentCompany && (
                  <div className="flex items-center gap-2 text-blue-400 text-sm">
                    <Building size={16} />
                    <span>{currentCompany.name}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="flex gap-3 flex-wrap">
            <button
              onClick={() => {
                resetExpenseForm();
                setShowExpenseModal(true);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
            >
              <Plus size={20} />
              Add Expense
            </button>
            <button
              onClick={forceRefresh}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
              disabled={loading}
            >
              <RefreshCw size={20} />
              Refresh
            </button>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                showFilters ? 'bg-orange-600 text-white' : 'bg-gray-700 hover:bg-gray-600 text-gray-300'
              }`}
            >
              <Filter size={20} />
              Filters
            </button>
            <button
              onClick={exportToCSV}
              className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors"
              disabled={saving}
            >
              <Download size={20} />
              Export
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          {/* View Mode Selector */}
          <div className="md:col-span-4 flex gap-2">
            <button
              onClick={() => setViewMode('all')}
              className={`flex-1 px-4 py-2 rounded-lg transition-colors ${
                viewMode === 'all' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              All Records ({filteredData.length})
            </button>
            <button
              onClick={() => setViewMode('income')}
              className={`flex-1 px-4 py-2 rounded-lg transition-colors ${
                viewMode === 'income' 
                  ? 'bg-green-600 text-white' 
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              Income ({summary.incomeCount})
            </button>
            <button
              onClick={() => setViewMode('expenses')}
              className={`flex-1 px-4 py-2 rounded-lg transition-colors ${
                viewMode === 'expenses' 
                  ? 'bg-red-600 text-white' 
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              Expenses ({summary.expenseCount})
            </button>
          </div>

          {/* Total Income */}
          <div className="bg-gradient-to-br from-green-900/40 to-green-800/20 border border-green-700 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-green-300 text-sm">Total Income</span>
              <TrendingUp size={20} className="text-green-400" />
            </div>
            <div className="text-2xl font-bold text-white">${summary.totalIncome.toFixed(2)}</div>
            <div className="text-xs text-green-400 mt-1">{summary.incomeCount} transactions</div>
          </div>

          {/* Total Expenses */}
          <div className="bg-gradient-to-br from-red-900/40 to-red-800/20 border border-red-700 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-red-300 text-sm">Total Expenses</span>
              <TrendingDown size={20} className="text-red-400" />
            </div>
            <div className="text-2xl font-bold text-white">${summary.totalExpenses.toFixed(2)}</div>
            <div className="text-xs text-red-400 mt-1">{summary.expenseCount} transactions</div>
          </div>

          {/* Net Profit */}
          <div className={`bg-gradient-to-br ${
            summary.netProfit >= 0 
              ? 'from-blue-900/40 to-blue-800/20 border-blue-700' 
              : 'from-orange-900/40 to-orange-800/20 border-orange-700'
          } border rounded-lg p-4`}>
            <div className="flex items-center justify-between mb-2">
              <span className={`${
                summary.netProfit >= 0 ? 'text-blue-300' : 'text-orange-300'
              } text-sm`}>Net Profit</span>
              <DollarSign size={20} className={summary.netProfit >= 0 ? 'text-blue-400' : 'text-orange-400'} />
            </div>
            <div className={`text-2xl font-bold ${
              summary.netProfit >= 0 ? 'text-white' : 'text-orange-400'
            }`}>
              ${Math.abs(summary.netProfit).toFixed(2)}
            </div>
            <div className={`text-xs ${
              summary.netProfit >= 0 ? 'text-blue-400' : 'text-orange-400'
            } mt-1`}>
              {summary.netProfit >= 0 ? 'Profit' : 'Loss'}
            </div>
          </div>

          {/* Record Count */}
          <div className="bg-gradient-to-br from-purple-900/40 to-purple-800/20 border border-purple-700 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-purple-300 text-sm">Total Records</span>
              <Activity size={20} className="text-purple-400" />
            </div>
            <div className="text-2xl font-bold text-white">{summary.recordCount}</div>
            <div className="text-xs text-purple-400 mt-1">Filtered results</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4 mb-6">
          <h3 className="text-lg font-semibold text-white mb-4">Filters</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div>
              <label className="block text-sm text-gray-300 mb-2">Search</label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search transactions..."
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400"
              />
            </div>

            {/* Bike Filter */}
            <div>
              <label className="block text-sm text-gray-300 mb-2">Bike</label>
              <select
                value={selectedBike}
                onChange={(e) => setSelectedBike(e.target.value)}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
              >
                <option value="all">All Bikes</option>
                {getUniqueBikes().map(bike => (
                  <option key={bike} value={bike}>{bike}</option>
                ))}
              </select>
            </div>

            {/* Month Filter */}
            <div>
              <label className="block text-sm text-gray-300 mb-2">Month</label>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
              >
                <option value="all">All Months</option>
                <option value="0">January</option>
                <option value="1">February</option>
                <option value="2">March</option>
                <option value="3">April</option>
                <option value="4">May</option>
                <option value="5">June</option>
                <option value="6">July</option>
                <option value="7">August</option>
                <option value="8">September</option>
                <option value="9">October</option>
                <option value="10">November</option>
                <option value="11">December</option>
              </select>
            </div>

            {/* Year Filter */}
            <div>
              <label className="block text-sm text-gray-300 mb-2">Year</label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
              >
                <option value="all">All Years</option>
                <option value="2025">2025</option>
                <option value="2024">2024</option>
                <option value="2023">2023</option>
              </select>
            </div>

            {/* Sort By */}
            <div>
              <label className="block text-sm text-gray-300 mb-2">Sort By</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
              >
                <option value="date">Date</option>
                <option value="amount">Amount</option>
              </select>
            </div>

            {/* Order */}
            <div>
              <label className="block text-sm text-gray-300 mb-2">Order</label>
              <select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
              >
                <option value="desc">Newest First</option>
                <option value="asc">Oldest First</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Records Table */}
      <div className="bg-gray-800/50 border border-gray-700 rounded-lg overflow-hidden">
        <div className="max-h-[600px] overflow-y-auto overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-700/50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Description</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Bike Model</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Bike Number</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-300 uppercase">Amount</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-300 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {filteredData.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center text-gray-400">
                    <AlertCircle className="mx-auto mb-2" size={32} />
                    <div>No transactions found</div>
                    <div className="text-sm mt-1">Try adjusting your filters or add expenses</div>
                  </td>
                </tr>
              ) : (
                filteredData.map((record) => (
                  <tr key={record.id} className="hover:bg-gray-700/30 transition-colors">
                    <td className="px-4 py-3 text-sm text-gray-300 whitespace-nowrap">
                      {formatDate(record.date)}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        record.type === 'income' 
                          ? 'bg-green-900/30 text-green-400 border border-green-700' 
                          : 'bg-red-900/30 text-red-400 border border-red-700'
                      }`}>
                        {record.type}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-300">
                      <div className="font-medium">{record.description}</div>
                      {record.customerName && record.customerName !== 'Unknown' && (
                        <div className="text-xs text-gray-500">{record.customerName}</div>
                      )}
                      {record.category && (
                        <div className="text-xs text-gray-500">{record.category}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-300">
                      <div className="font-medium">{record.bikeModel}</div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-400">
                      {record.motorbikeNumber}
                    </td>
                    <td className="px-4 py-3 text-sm text-right font-medium whitespace-nowrap">
                      <span className={record.type === 'income' ? 'text-green-400' : 'text-red-400'}>
                        {record.type === 'income' ? '+' : '-'}${Math.abs(record.amount).toFixed(2)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-center">
                      <div className="flex items-center justify-center gap-2">
                        {record.type === 'income' && record.invoice && (
                          <button
                            onClick={() => handleViewInvoice(record)}
                            className="inline-flex items-center px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs transition-colors"
                          >
                            <Eye size={14} className="mr-1" />
                            View
                          </button>
                        )}
                        {record.type === 'expense' && (
                          <button
                            onClick={() => handleDeleteExpense(record.id)}
                            className="inline-flex items-center px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-xs transition-colors"
                            disabled={saving}
                          >
                            <Trash2 size={14} className="mr-1" />
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Expense Modal */}
      {showExpenseModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-white">Add Expense</h3>
                <button
                  onClick={() => setShowExpenseModal(false)}
                  className="text-gray-400 hover:text-white"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="space-y-4">
                {/* Bike Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Select Bike *
                  </label>
                  <select
                    value={expenseForm.bikeId}
                    onChange={(e) => handleExpenseFormChange('bikeId', e.target.value)}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                    required
                  >
                    <option value="">-- Select a bike --</option>
                    {bikes.map(bike => (
                      <option key={bike.id} value={bike.id}>
                        {bike.regNumber} - {bike.model}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Category */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Category *
                  </label>
                  <select
                    value={expenseForm.category}
                    onChange={(e) => handleExpenseFormChange('category', e.target.value)}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                    required
                  >
                    {expenseCategories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>

                {/* Amount */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Amount ($) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={expenseForm.amount}
                    onChange={(e) => handleExpenseFormChange('amount', e.target.value)}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                    placeholder="0.00"
                    required
                  />
                </div>

                {/* Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Date *
                  </label>
                  <input
                    type="date"
                    value={expenseForm.date}
                    onChange={(e) => handleExpenseFormChange('date', e.target.value)}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                    required
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Description *
                  </label>
                  <input
                    type="text"
                    value={expenseForm.description}
                    onChange={(e) => handleExpenseFormChange('description', e.target.value)}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                    placeholder="e.g., Oil change, Tire replacement"
                    required
                  />
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Notes (Optional)
                  </label>
                  <textarea
                    value={expenseForm.notes}
                    onChange={(e) => handleExpenseFormChange('notes', e.target.value)}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                    placeholder="Additional details..."
                    rows="3"
                  />
                </div>

                {/* Buttons */}
                <div className="flex gap-3 pt-4">
                  <button
                    onClick={handleAddExpense}
                    disabled={saving}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {saving ? (
                      <>
                        <Loader size={20} className="animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save size={20} />
                        Save Expense
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => setShowExpenseModal(false)}
                    disabled={saving}
                    className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Invoice Modal - Keep existing implementation */}
      {showInvoiceModal && selectedInvoice && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-white">Invoice Details</h3>
                <button
                  onClick={() => setShowInvoiceModal(false)}
                  className="text-gray-400 hover:text-white"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-gray-400 text-sm">Invoice Number</label>
                    <div className="text-white font-medium">{selectedInvoice.invoiceNumber}</div>
                  </div>
                  <div>
                    <label className="text-gray-400 text-sm">Customer</label>
                    <div className="text-white font-medium">{selectedInvoice.customerName}</div>
                  </div>
                  <div>
                    <label className="text-gray-400 text-sm">Bike</label>
                    <div className="text-white font-medium">{getBikeModel(selectedInvoice)} ({getBikeRegNumber(selectedInvoice)})</div>
                  </div>
                  <div>
                    <label className="text-gray-400 text-sm">Amount</label>
                    <div className="text-green-400 font-bold text-lg">${getInvoiceAmount(selectedInvoice).toFixed(2)}</div>
                  </div>
                </div>

                <button
                  onClick={() => setShowInvoiceModal(false)}
                  className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FinanceManager;